import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchOrdersAction } from "../../../redux/slices/orders/ordersSlices";
import ErrorMsg from "../../ErrorMessage/ErrorMsg";
import LoadingComponent from "../../LoadingComp/LoadingComponent";
import NoDataFound from "../../NoDataFound/NoDataFound";
import SuccessMsg from "../../SuccessMsg/SuccessMsg";
import OrdersStats from "./OrdersStatistics";

export default function OrdersList() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  
  useEffect(() => {
    dispatch(fetchOrdersAction());
  }, [dispatch]);
  
  //get data from store
  const {
    error,
    loading,
    orders: { orders },
  } = useSelector((state) => state?.orders);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch(status) {
      case 'PENDIENTE':
        return 'bg-yellow-100 text-yellow-800';
      case 'CONFIRMADO':
        return 'bg-blue-100 text-blue-800';
      case 'ENVIADO':
        return 'bg-purple-100 text-purple-800';
      case 'ENTREGADO':
        return 'bg-green-100 text-green-800';
      case 'CANCELADO':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get payment status badge
  const getPaymentStatusBadge = (status) => {
    switch(status) {
      case 'PAGADO':
        return 'bg-green-100 text-green-800';
      case 'NO PAGADO':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get translated status
  const getTranslatedStatus = (status) => {
    switch(status) {
      case 'PENDIENTE':
        return t('orders.pending');
      case 'CONFIRMADO':
        return t('orders.confirmed');
      case 'ENVIADO':
        return t('orders.shipped');
      case 'ENTREGADO':
        return t('orders.delivered');
      case 'CANCELADO':
        return t('orders.cancelled');
      default:
        return status;
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-center items-center h-64">
          <LoadingComponent />
        </div>
      </div>
    );
  }

  return (
    <>
      {error && <ErrorMsg message={error?.message} />}
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="sm:flex sm:items-center"></div>
        {/* order stats */}
        <OrdersStats />

        <h3 className="text-lg font-medium leading-6 text-gray-900 mt-3">
          {t('orders.recentOrders')}
        </h3>
        <div className="-mx-4 mt-3 overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:-mx-6 md:mx-0 md:rounded-lg">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                    {t('orders.orderId')}
                  </th>
                  <th
                    scope="col"
                    className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 lg:table-cell">
                    {t('orders.paymentStatus')}
                  </th>
                  <th
                    scope="col"
                    className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell">
                    {t('orders.orderDate')}
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    {t('orders.deliveryDate')}
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    {t('orders.status')}
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    {t('orders.total')}
                  </th>
                  <th
                    scope="col"
                    className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                    <span className="sr-only">{t('orders.edit')}</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {orders?.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-12">
                      <NoDataFound message={t('orders.noOrdersFound')} />
                    </td>
                  </tr>
                ) : (
                  orders?.map((order) => (
                    <tr key={order._id} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                        {order.orderNumber}
                      </td>
                      <td className="hidden whitespace-nowrap px-3 py-4 text-sm lg:table-cell">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPaymentStatusBadge(order.paymentStatus)}`}>
                          {order.paymentStatus === "PAGADO" ? t('orders.paid') : t('orders.notPaid')}
                        </span>
                      </td>
                      <td className="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 lg:table-cell">
                        {formatDate(order?.createdAt)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {order.deliveryDate ? formatDate(order.deliveryDate) : t('orders.pending')}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClass(order?.status)}`}>
                          {getTranslatedStatus(order?.status)}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">
                        ${order?.totalPrice}
                      </td>
                      <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                        {order?.paymentStatus === "NO PAGADO" ? (
                          <span className="text-gray-400 cursor-not-allowed">
                            {t('orders.edit')}
                          </span>
                        ) : (
                          <Link
                            to={`/admin/orders/${order?._id}`}
                            className="text-orange-600 hover:text-orange-900 font-medium">
                            {t('orders.edit')}
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}